// Sync between the local mirror and D1.
//
// Ingestion happens at a desktop and review happens on a phone, which is a
// genuine sync requirement rather than premature engineering. Review itself
// must work offline: podcast listening happens where phone signal does not.

import * as api from "./api";
import * as db from "./db";
import type { Card, LangProfile, Lang, Review } from "./types";
import { DEFAULT_PROFILES, LANGS } from "./types";

const LAST_SYNC = "lastSync";

export interface SyncState {
  online: boolean;
  pending: number;
  lastSync: number | null;
  error: string | null;
}

/** Queue an answered card locally. The outbox drains on the next successful push. */
export async function recordAnswer(card: Card, review: Review): Promise<void> {
  await Promise.all([
    db.put("cards", card),
    db.put("outboxCards", card),
    db.put("outboxReviews", review),
  ]);
}

interface QueuedCapture { lang: Lang; text: string }

/**
 * Capture a word. Words get jotted down while listening, which happens in
 * places with no signal, so a failed call queues locally instead of being lost.
 */
export async function captureWord(lang: Lang, text: string): Promise<{ queued: boolean }> {
  try {
    await api.capture(lang, text);
    return { queued: false };
  } catch {
    const queue = await db.getMeta<QueuedCapture[]>("captureQueue", []);
    await db.setMeta("captureQueue", [...queue, { lang, text }]);
    return { queued: true };
  }
}

export async function drainCaptures(): Promise<number> {
  const queue = await db.getMeta<QueuedCapture[]>("captureQueue", []);
  if (queue.length === 0) return 0;
  const stuck: QueuedCapture[] = [];
  let sent = 0;
  for (const item of queue) {
    try {
      await api.capture(item.lang, item.text);
      sent++;
    } catch {
      stuck.push(item);
    }
  }
  await db.setMeta("captureQueue", stuck);
  return sent;
}

export async function queuedCaptureCount(): Promise<number> {
  return (await db.getMeta<QueuedCapture[]>("captureQueue", [])).length;
}

/** Send anything queued. Safe to call when offline — it just reports back. */
export async function pushOutbox(): Promise<{ pushed: number }> {
  const [reviews, cards] = await Promise.all([db.loadOutboxReviews(), db.loadOutboxCards()]);
  if (reviews.length === 0 && cards.length === 0) return { pushed: 0 };

  await api.push({ reviews, cards });
  // Only clear once the server has accepted them. Review ids are generated on
  // the client, so a retry after a dropped connection cannot double-count.
  await Promise.all([
    db.removeMany("outboxReviews", reviews.map((r) => r.id)),
    db.removeMany("outboxCards", cards.map((c) => c.id)),
  ]);
  return { pushed: reviews.length + cards.length };
}

/** Fetch everything changed since the last pull into the local mirror. */
export async function pullChanges(): Promise<{ pulled: number }> {
  const since = await db.getMeta<number>(LAST_SYNC, 0);
  const payload = await api.pull(since);

  await Promise.all([
    db.putMany("sources", payload.sources),
    db.putMany("notes", payload.notes),
    db.putMany("cards", payload.cards),
  ]);

  for (const lang of LANGS) {
    const stored = payload.settings[`profile:${lang}`];
    if (stored && typeof stored === "object") {
      await db.setMeta(`profile:${lang}`, { ...DEFAULT_PROFILES[lang], ...stored });
    }
  }

  await db.setMeta(LAST_SYNC, payload.now);
  return { pulled: payload.sources.length + payload.notes.length + payload.cards.length };
}

/** Push then pull. Push first so local answers are not clobbered by the pull. */
export async function syncNow(): Promise<{ pushed: number; pulled: number }> {
  await drainCaptures();
  const { pushed } = await pushOutbox();
  const { pulled } = await pullChanges();
  return { pushed, pulled };
}

export async function pendingCount(): Promise<number> {
  const [reviews, cards] = await Promise.all([db.loadOutboxReviews(), db.loadOutboxCards()]);
  return reviews.length + cards.length;
}

export async function lastSyncedAt(): Promise<number | null> {
  const value = await db.getMeta<number>(LAST_SYNC, 0);
  return value || null;
}

export async function loadProfile(lang: Lang): Promise<LangProfile> {
  return db.getMeta<LangProfile>(`profile:${lang}`, DEFAULT_PROFILES[lang]);
}

export async function saveProfile(lang: Lang, profile: LangProfile): Promise<void> {
  await db.setMeta(`profile:${lang}`, profile);
  await api.saveSettings({ [`profile:${lang}`]: profile });
}
